import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  UserCheck,
  User,
  Clock,
  Timer,
  Users,
  Calendar,
  Activity,
  CheckCircle,
  AlertCircle,
  LogIn,
  LogOut,
  Plus,
  Edit,
  Trash2,
  Phone,
  Mail,
  MapPin,
  Globe,
  Star,
  Search,
  Shield,
  FileText,
  BarChart3,
  Save,
  Download,
  Printer,
  RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';
import { escapeDeep } from '../lib/escapeHtml';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StaffManagement = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  
  // Community Services State
  const [communityServices, setCommunityServices] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState(null);
  
  // Time Clock State
  const [clockedIn, setClockedIn] = useState(false);
  const [clockInTime, setClockInTime] = useState(null);
  const [clockOutTime, setClockOutTime] = useState(null);
  const [shiftInfo, setShiftInfo] = useState({
    shift: 'Day',
    color: 'bg-green-100 text-green-800'
  });
  const [shiftNote, setShiftNote] = useState('');
  const [timeRecords, setTimeRecords] = useState([]);
  
  // Staff State
  const [allStaff, setAllStaff] = useState([]);
  const [staffProfile, setStaffProfile] = useState({
    username: '',
    email: '',
    phone: '',
    full_name: '',
    password: '',
    role: 'staff'
  });

  // Schedule State
  const [schedules, setSchedules] = useState([]);
  const [newSchedule, setNewSchedule] = useState({
    staff_name: '',
    date: '',
    shift: '',
    task: ''
  });
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [scheduleView, setScheduleView] = useState('list');

  // Call-in tracking state
  const [showCallInDialog, setShowCallInDialog] = useState(false);
  const [selectedScheduleForCallIn, setSelectedScheduleForCallIn] = useState(null);
  const [callInData, setCallInData] = useState({
    status: 'called_in',
    notes: '',
    called_in_at: new Date().toISOString().slice(0, 16)
  });

  // Replacement staff state
  const [showReplacementDialog, setShowReplacementDialog] = useState(false);
  const [selectedScheduleForReplacement, setSelectedScheduleForReplacement] = useState(null);
  const [replacementData, setReplacementData] = useState({
    replacement_staff_id: '',
    replacement_staff_name: '',
    notes: ''
  });

  // UI State
  const [showAddStaffDialog, setShowAddStaffDialog] = useState(false);
  const [showAddScheduleDialog, setShowAddScheduleDialog] = useState(false);
  const [showTimeReports, setShowTimeReports] = useState(false);
  const [showShiftNoteDialog, setShowShiftNoteDialog] = useState(false);
  const [showPasswordResetDialog, setShowPasswordResetDialog] = useState(false);
  const [showDeleteScheduleDialog, setShowDeleteScheduleDialog] = useState(false);
  const [showAddServiceDialog, setShowAddServiceDialog] = useState(false);
  const [scheduleToDelete, setScheduleToDelete] = useState(null);
  const [staffToReset, setStaffToReset] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  // Community Service State
  const [newService, setNewService] = useState({
    name: '',
    category: '',
    description: '',
    phone: '',
    email: '',
    address: '',
    website: ''
  });

  // Load data on component mount
  useEffect(() => {
    fetchCommunityServices();
    fetchAllStaff();
    fetchSchedules();
    fetchTimeRecords();
    checkClockStatus();
    
    // Determine shift based on current time
    const now = new Date();
    const hour = now.getHours();
    
    if (hour >= 6 && hour < 14) {
      setShiftInfo({
        shift: 'morning',
        color: 'bg-green-100 text-green-800'
      });
    } else if (hour >= 14 && hour < 22) {
      setShiftInfo({
        shift: 'evening',
        color: 'bg-blue-100 text-blue-800'
      });
    } else {
      setShiftInfo({
        shift: 'night',
        color: 'bg-purple-100 text-purple-800'
      });
    }
  }, []);

  // Helper function to get shift times and colors
  const getShiftTimes = (shift) => {
    switch(shift) {
      case 'morning': return { start_time: '08:00', end_time: '16:00', display: '8am-4pm', color: 'bg-yellow-100 border-yellow-300 text-yellow-800' };
      case 'day': return { start_time: '08:00', end_time: '16:00', display: '8am-4pm', color: 'bg-blue-100 border-blue-300 text-blue-800' };
      case 'evening': return { start_time: '16:00', end_time: '00:00', display: '4pm-12am', color: 'bg-orange-100 border-orange-300 text-orange-800' };
      case 'night': return { start_time: '00:00', end_time: '08:00', display: '12am-8am', color: 'bg-purple-100 border-purple-300 text-purple-800' };
      case 'volunteer': return { start_time: '09:00', end_time: '17:00', display: '9am-5pm', color: 'bg-green-100 border-green-300 text-green-800' };
      case 'custom': return { start_time: '08:00', end_time: '16:00', display: '8am-4pm', color: 'bg-gray-100 border-gray-300 text-gray-800' };
      default: return { start_time: '08:00', end_time: '16:00', display: '8am-4pm', color: 'bg-gray-100 border-gray-300 text-gray-800' };
    }
  };

  const checkClockStatus = async () => {
    try {
      const response = await axios.get(`${API}/staff/time-clock-status`);
      const status = response.data;
      setClockedIn(status.is_clocked_in || false);
      if (status.is_clocked_in && status.clock_in_time) {
        const clockInDate = new Date(status.clock_in_time);
        setClockInTime(clockInDate.toLocaleTimeString());
      }
    } catch (error) {
      console.error('Error checking clock status:', error);
    }
  };

  // API Functions
  const fetchAllStaff = async () => {
    try {
      const response = await axios.get(`${API}/staff`);
      setAllStaff(response.data);
    } catch (error) {
      console.error('Error fetching staff:', error);
      toast.error('Failed to load staff');
    }
  };

  const fetchSchedules = async () => {
    try {
      const response = await axios.get(`${API}/schedules`);
      setSchedules(response.data);
    } catch (error) {
      console.error('Error fetching schedules:', error);
    }
  };

  const fetchTimeRecords = async () => {
    try {
      // Use the admin endpoint to get all time entries for reports
      const response = await axios.get(`${API}/time-reports/daily`);
      setTimeRecords(response.data.time_entries || []);
    } catch (error) {
      console.error('Error fetching time records:', error);
      // Try alternative endpoint for user's own records
      try {
        const userResponse = await axios.get(`${API}/staff/${user?.id}/time-entries`);
        setTimeRecords(userResponse.data.time_entries || []);
      } catch (err) {
        console.error('Error fetching user time records:', err);
      }
    }
  };

  const fetchCommunityServices = async () => {
    try {
      const response = await axios.get(`${API}/community-services`);
      setCommunityServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
      toast.error('Failed to load community services');
    }
  };

  // Community Services Functions
  const handleDeleteClick = (service) => {
    setServiceToDelete(service);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (serviceToDelete) {
      axios.delete(`${API}/community-services/${serviceToDelete.id}`)
        .then(() => {
          toast.success(`${serviceToDelete.name} deleted successfully!`);
          fetchCommunityServices();
          setShowDeleteConfirm(false);
          setServiceToDelete(null);
        })
        .catch((error) => {
          toast.error(`Delete failed: ${error.message}`);
          setShowDeleteConfirm(false);
          setServiceToDelete(null);
        });
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setServiceToDelete(null);
  };

  // Add Community Service Function
  const handleAddService = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/community-services`, newService);
      toast.success(`${newService.name} added successfully`);
      setShowAddServiceDialog(false);
      setNewService({
        name: '',
        category: '',
        description: '',
        phone: '',
        email: '',
        address: '',
        website: ''
      });
      fetchCommunityServices();
    } catch (error) {
      console.error('Error adding service:', error);
      toast.error(error.response?.data?.detail || 'Failed to add community service');
    }
  };

  // Time Clock Functions
  const handleClockIn = async () => {
    const now = new Date();
    const timeString = now.toLocaleTimeString();
    
    try {
      await axios.post(`${API}/staff/clock-in`, {
        shift_type: shiftInfo.shift
      });
      
      setClockedIn(true);
      setClockInTime(timeString);
      setClockOutTime(null);
      toast.success(`Clocked in at ${timeString} for ${shiftInfo.shift} shift`);
      fetchTimeRecords();
    } catch (error) {
      console.error('Error clocking in:', error);
      toast.error('Failed to clock in');
    }
  };

  const handleClockOut = async () => {
    const now = new Date();
    const timeString = now.toLocaleTimeString();
    
    if (!clockInTime) {
      toast.error('You must clock in first');
      return;
    }

    if (shiftNote.trim()) {
      setShowShiftNoteDialog(true);
      return;
    }

    await completeClockOut(timeString, '');
  };

  const completeClockOut = async (timeString, note) => {
    try {
      // Use end-shift endpoint to create proper shift logs
      await axios.post(`${API}/staff/end-shift`, {
        shift_notes: note || shiftNote,
        tasks_completed: `Shift completed at ${timeString}`,
        resident_count: null // Could be enhanced to ask for resident count
      });
      
      setClockedIn(false);
      setClockOutTime(timeString);
      setShiftNote('');
      toast.success(`Shift ended at ${timeString}. Shift log created successfully.`);
      fetchTimeRecords();
    } catch (error) {
      console.error('Error ending shift:', error);
      toast.error('Failed to end shift and create shift log');
    }
  };

  const handleShiftNoteSubmit = () => {
    const now = new Date();
    const timeString = now.toLocaleTimeString();
    completeClockOut(timeString, shiftNote);
    setShowShiftNoteDialog(false);
  };

  // Staff Functions
  const handleAddStaff = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/staff`, staffProfile);
      toast.success(`${staffProfile.full_name} added successfully`);
      setShowAddStaffDialog(false);
      setStaffProfile({
        username: '',
        email: '',
        phone: '',
        full_name: '',
        password: '',
        role: 'staff'
      });
      fetchAllStaff();
    } catch (error) {
      console.error('Error adding staff:', error);
      toast.error(error.response?.data?.detail || 'Failed to add staff member');
    }
  };

  const handleEditStaff = (staff) => {
    setStaffProfile({
      username: staff.username,
      email: staff.email || '',
      phone: staff.phone || '',
      full_name: staff.full_name,
      password: '',
      role: staff.role,
      id: staff.id
    });
    setShowAddStaffDialog(true);
  };

  const handleUpdateStaff = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/staff/${staffProfile.id}`, {
        username: staffProfile.username,
        email: staffProfile.email,
        phone: staffProfile.phone,
        full_name: staffProfile.full_name,
        role: staffProfile.role
      });
      toast.success(`${staffProfile.full_name} updated successfully`);
      setShowAddStaffDialog(false);
      setStaffProfile({
        username: '',
        email: '',
        phone: '',
        full_name: '',
        password: '',
        role: 'staff'
      });
      fetchAllStaff();
    } catch (error) {
      console.error('Error updating staff:', error);
      toast.error(error.response?.data?.detail || 'Failed to update staff member');
    }
  };

  const handleDeleteStaff = async (staffId) => {
    if (window.confirm('Are you sure you want to delete this staff member? This action cannot be undone.')) {
      try {
        await axios.delete(`${API}/staff/${staffId}`);
        toast.success('Staff member deleted successfully');
        fetchAllStaff();
      } catch (error) {
        console.error('Error deleting staff:', error);
        toast.error('Failed to delete staff member');
      }
    }
  };

  const handleResetPassword = (staff) => {
    setStaffToReset(staff);
    setNewPassword('');
    setShowPasswordResetDialog(true);
  };

  const handlePasswordReset = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }

    try {
      await axios.put(`${API}/staff/${staffToReset.id}/reset-password`, {
        new_password: newPassword
      });
      toast.success(`Password reset successfully for ${staffToReset.full_name}`);
      setShowPasswordResetDialog(false);
      setStaffToReset(null);
      setNewPassword('');
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error('Failed to reset password');
    }
  };

  // Schedule Functions
  const handleAddSchedule = async (e) => {
    e.preventDefault();
    try {
      // Find the selected staff member to get their ID
      const selectedStaff = allStaff.find(staff => staff.full_name === newSchedule.staff_name);
      if (!selectedStaff) {
        toast.error('Please select a valid staff member');
        return;
      }

      // Validate date
      if (!newSchedule.date) {
        toast.error('Please select a date');
        return;
      }

      // Map shift to time ranges
      const getShiftTimes = (shift) => {
        switch(shift) {
          case 'morning': return { start_time: '08:00', end_time: '16:00', display: '8am-4pm', color: 'bg-yellow-100 border-yellow-300 text-yellow-800' };
          case 'day': return { start_time: '08:00', end_time: '16:00', display: '8am-4pm', color: 'bg-blue-100 border-blue-300 text-blue-800' };
          case 'evening': return { start_time: '16:00', end_time: '00:00', display: '4pm-12am', color: 'bg-orange-100 border-orange-300 text-orange-800' };
          case 'night': return { start_time: '00:00', end_time: '08:00', display: '12am-8am', color: 'bg-purple-100 border-purple-300 text-purple-800' };
          case 'volunteer': return { start_time: '09:00', end_time: '17:00', display: '9am-5pm', color: 'bg-green-100 border-green-300 text-green-800' };
          case 'custom': return { start_time: '08:00', end_time: '16:00', display: '8am-4pm', color: 'bg-gray-100 border-gray-300 text-gray-800' };
          default: return { start_time: '08:00', end_time: '16:00', display: '8am-4pm', color: 'bg-gray-100 border-gray-300 text-gray-800' };
        }
      };

      const shiftTimes = getShiftTimes(newSchedule.shift);

      const scheduleData = {
        staff_id: selectedStaff.id,
        date: newSchedule.date, // Use the actual date selected
        shift_type: newSchedule.shift,
        start_time: shiftTimes.start_time,
        end_time: shiftTimes.end_time,
        notes: newSchedule.task || ''
      };

      await axios.post(`${API}/schedules`, scheduleData);
      toast.success('Schedule added successfully');
      setShowAddScheduleDialog(false);
      setNewSchedule({
        staff_name: '',
        date: '',
        shift: '',
        task: ''
      });
      fetchSchedules();
    } catch (error) {
      console.error('Error adding schedule:', error);
      toast.error(error.response?.data?.detail || 'Failed to add schedule');
    }
  };

  const handleEditSchedule = (schedule) => {
    setEditingSchedule(schedule);
    setNewSchedule({
      staff_name: schedule.staff_name,
      date: schedule.date,
      shift: schedule.shift_type || schedule.shift,
      task: schedule.notes || schedule.task
    });
    setShowAddScheduleDialog(true);
  };

  const handleUpdateSchedule = async (e) => {
    e.preventDefault();
    try {
      // Find the selected staff member to get their ID
      const selectedStaff = allStaff.find(staff => staff.full_name === newSchedule.staff_name);
      if (!selectedStaff) {
        toast.error('Please select a valid staff member');
        return;
      }

      // Map shift to time ranges
      const getShiftTimes = (shift) => {
        switch(shift) {
          case 'morning': return { start_time: '08:00', end_time: '16:00', display: '8am-4pm', color: 'bg-yellow-100 border-yellow-300 text-yellow-800' };
          case 'day': return { start_time: '08:00', end_time: '16:00', display: '8am-4pm', color: 'bg-blue-100 border-blue-300 text-blue-800' };
          case 'evening': return { start_time: '16:00', end_time: '00:00', display: '4pm-12am', color: 'bg-orange-100 border-orange-300 text-orange-800' };
          case 'night': return { start_time: '00:00', end_time: '08:00', display: '12am-8am', color: 'bg-purple-100 border-purple-300 text-purple-800' };
          case 'volunteer': return { start_time: '09:00', end_time: '17:00', display: '9am-5pm', color: 'bg-green-100 border-green-300 text-green-800' };
          case 'custom': return { start_time: '08:00', end_time: '16:00', display: '8am-4pm', color: 'bg-gray-100 border-gray-300 text-gray-800' };
          default: return { start_time: '08:00', end_time: '16:00', display: '8am-4pm', color: 'bg-gray-100 border-gray-300 text-gray-800' };
        }
      };

      const shiftTimes = getShiftTimes(newSchedule.shift);

      const scheduleData = {
        staff_id: selectedStaff.id,
        date: newSchedule.date,
        shift_type: newSchedule.shift,
        start_time: shiftTimes.start_time,
        end_time: shiftTimes.end_time,
        notes: newSchedule.task || ''
      };

      await axios.put(`${API}/schedules/${editingSchedule.id}`, scheduleData);
      toast.success('Schedule updated successfully');
      setShowAddScheduleDialog(false);
      setEditingSchedule(null);
      setNewSchedule({
        staff_name: '',
        date: '',
        shift: '',
        task: ''
      });
      fetchSchedules();
    } catch (error) {
      console.error('Error updating schedule:', error);
      toast.error('Failed to update schedule');
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    const schedule = schedules.find(s => s.id === scheduleId);
    setScheduleToDelete(schedule);
    setShowDeleteScheduleDialog(true);
  };

  const confirmDeleteSchedule = async () => {
    if (!scheduleToDelete) return;
    
    try {
      await axios.delete(`${API}/schedules/${scheduleToDelete.id}`);
      toast.success('Schedule deleted successfully');
      setShowDeleteScheduleDialog(false);
      setScheduleToDelete(null);
      fetchSchedules();
    } catch (error) {
      console.error('Error deleting schedule:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete schedule');
      setShowDeleteScheduleDialog(false);
      setScheduleToDelete(null);
    }
  };

  const cancelDeleteSchedule = () => {
    setShowDeleteScheduleDialog(false);
    setScheduleToDelete(null);
  };

  // Call-in Functions
  const handleCallInSubmit = async () => {
    if (!selectedScheduleForCallIn) return;
    
    try {
      // Update the schedule with call-in status
      await axios.put(`${API}/schedules/${selectedScheduleForCallIn.id}`, {
        ...selectedScheduleForCallIn,
        called_in_status: callInData.status,
        called_in_notes: callInData.notes,
        called_in_at: callInData.called_in_at
      });
      
      toast.success(`Call-in status updated for ${selectedScheduleForCallIn.staff_name}`);
      setShowCallInDialog(false);
      setSelectedScheduleForCallIn(null);
      setCallInData({
        status: 'called_in',
        notes: '',
        called_in_at: new Date().toISOString().slice(0, 16)
      });
      fetchSchedules();
    } catch (error) {
      console.error('Error updating call-in status:', error);
      toast.error('Failed to update call-in status');
    }
  };

  const cancelCallIn = () => {
    setShowCallInDialog(false);
    setSelectedScheduleForCallIn(null);
    setCallInData({
      status: 'called_in',
      notes: '',
      called_in_at: new Date().toISOString().slice(0, 16)
    });
  };

  // Replacement Staff Functions
  const handleReplacementSubmit = async () => {
    if (!selectedScheduleForReplacement || !replacementData.replacement_staff_id) return;

    try {
      await axios.put(`${API}/schedules/${selectedScheduleForReplacement.id}/replacement`, {
        replacement_staff_id: replacementData.replacement_staff_id,
        replacement_staff_name: replacementData.replacement_staff_name,
        notes: replacementData.notes
      });

      toast.success(`Replacement staff assigned for ${selectedScheduleForReplacement.staff_name}`);
      setShowReplacementDialog(false);
      setSelectedScheduleForReplacement(null);
      setReplacementData({
        replacement_staff_id: '',
        replacement_staff_name: '',
        notes: ''
      });
      
      // Refresh schedules to show updates
      fetchSchedules();
    } catch (error) {
      console.error('Error assigning replacement staff:', error);
      toast.error('Failed to assign replacement staff');
    }
  };

  const cancelReplacement = () => {
    setShowReplacementDialog(false);
    setSelectedScheduleForReplacement(null);
    setReplacementData({
      replacement_staff_id: '',
      replacement_staff_name: '',
      notes: ''
    });
  };

  const exportTimeReports = () => {
    const csvData = timeRecords.map(record => ({
      Staff: record.staff_name,
      Date: record.date,
      'Clock In': record.clock_in_time,
      'Clock Out': record.clock_out_time,
      'Hours Worked': record.hours_worked || 'In Progress',
      'Shift Type': record.shift_type,
      'Shift Note': record.shift_note || ''
    }));

    const csvContent = [
      Object.keys(csvData[0]).join(','),
      ...csvData.map(row => Object.values(row).map(val => `"${val}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `time_reports_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const printStaffSchedules = () => {
    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    
    // Group schedules by staff member and date (same logic as display)
    const groupedSchedules = {};
    escapeDeep(schedules).forEach(schedule => {
      const key = `${schedule.staff_name}_${schedule.date}`;
      if (!groupedSchedules[key]) {
        groupedSchedules[key] = {
          staff_name: schedule.staff_name,
          date: schedule.date,
          shifts: []
        };
      }
      groupedSchedules[key].shifts.push(schedule);
    });

    // Sort shifts within each group by start time
    Object.values(groupedSchedules).forEach(group => {
      group.shifts.sort((a, b) => {
        const aTime = a.start_time || '00:00';
        const bTime = b.start_time || '00:00';
        return aTime.localeCompare(bTime);
      });
    });

    const sortedGroups = Object.values(groupedSchedules).sort((a, b) => {
      const dateCompare = a.date.localeCompare(b.date);
      if (dateCompare !== 0) return dateCompare;
      return a.staff_name.localeCompare(b.staff_name);
    });

    // Generate HTML content for printing
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Staff Schedules - ${new Date().toLocaleDateString()}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 10px; }
            .schedule-group { margin-bottom: 25px; page-break-inside: avoid; }
            .staff-header { background-color: #f5f5f5; padding: 10px; border-left: 4px solid #4f46e5; margin-bottom: 10px; }
            .staff-name { font-size: 18px; font-weight: bold; margin: 0; }
            .date { color: #666; font-size: 14px; margin: 5px 0 0 0; }
            .shift { background-color: #fafafa; padding: 8px; margin: 5px 0; border-radius: 4px; border: 1px solid #e0e0e0; }
            .shift-header { font-weight: bold; color: #4f46e5; }
            .shift-time { color: #666; font-size: 14px; }
            .shift-notes { color: #555; font-size: 13px; margin-top: 5px; }
            .summary { color: #888; font-size: 12px; margin-top: 10px; padding-top: 10px; border-top: 1px solid #ddd; }
            @media print {
              body { margin: 0; }
              .schedule-group { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Staff Schedules</h1>
            <p>Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</p>
            <p>Total: ${sortedGroups.length} schedule entries</p>
          </div>
          
          ${sortedGroups.map(group => `
            <div class="schedule-group">
              <div class="staff-header">
                <h3 class="staff-name">${group.staff_name}</h3>
                <p class="date">${(() => {
                  const [year, month, day] = group.date.split('-');
                  const localDate = new Date(year, month - 1, day);
                  return localDate.toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  });
                })()}</p>
              </div>
              
              ${group.shifts.map((schedule, shiftIndex) => `
                <div class="shift">
                  <div class="shift-header">
                    Shift ${shiftIndex + 1}: ${(schedule.shift_type || schedule.shift).charAt(0).toUpperCase() + 
                                               (schedule.shift_type || schedule.shift).slice(1)}
                  </div>
                  <div class="shift-time">
                    Time: ${schedule.start_time} - ${schedule.end_time}
                  </div>
                  ${(schedule.notes || schedule.task) ? `
                    <div class="shift-notes">
                      Notes: ${schedule.notes || schedule.task}
                    </div>
                  ` : ''}
                </div>
              `).join('')}
              
              ${group.shifts.length > 1 ? `
                <div class="summary">
                  Total: ${group.shifts.length} shifts • 
                  ${(() => {
                    const totalMinutes = group.shifts.reduce((total, shift) => {
                      const start = shift.start_time.split(':');
                      const end = shift.end_time.split(':');
                      const startMinutes = parseInt(start[0]) * 60 + parseInt(start[1]);
                      let endMinutes = parseInt(end[0]) * 60 + parseInt(end[1]);
                      if (endMinutes <= startMinutes) endMinutes += 24 * 60;
                      return total + (endMinutes - startMinutes);
                    }, 0);
                    const hours = Math.floor(totalMinutes / 60);
                    const minutes = totalMinutes % 60;
                    return `${hours}h ${minutes}m total`;
                  })()}
                </div>
              ` : ''}
            </div>
          `).join('')}
          
          ${sortedGroups.length === 0 ? '<p style="text-align: center; color: #666; margin-top: 50px;">No schedules available to print.</p>' : ''}
        </body>
      </html>
    `;

    // Write content to print window and trigger print
    printWindow.document.write(printContent);
    printWindow.document.close();
    
    // Wait for content to load then print
    printWindow.onload = () => {
      printWindow.print();
      printWindow.close();
    };
    
    toast.success('Print dialog opened');
  };

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
            <UserCheck className="h-8 w-8 text-indigo-600" />
            <span>Staff Management</span>
          </h1>
          <p className="text-gray-600 mt-1">Time tracking, staff coordination, and community services</p>
        </div>
        
        <div className="mt-4 sm:mt-0 flex items-center space-x-2">
          <Badge className={shiftInfo.color}>
            {shiftInfo.shift} Shift
          </Badge>
          <Badge variant="outline" className="px-3 py-1">
            {user?.role?.replace('_', ' ').toUpperCase()}
          </Badge>
        </div>
      </div>

      {/* Main Tabs */}
      <Tabs defaultValue="timeclock" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="timeclock">Time Clock</TabsTrigger>
          <TabsTrigger value="staff">Staff Directory</TabsTrigger>
          <TabsTrigger value="schedules">Schedules</TabsTrigger>
          <TabsTrigger value="community">Community Directory</TabsTrigger>
        </TabsList>

        {/* Time Clock Tab */}
        <TabsContent value="timeclock" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Clock In/Out Card */}
            <Card className="card-shadow lg:col-span-1">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Clock className="h-5 w-5 text-indigo-600" />
                  <span>Time Clock</span>
                </CardTitle>
                <CardDescription>
                  {clockedIn ? `Clocked in at ${clockInTime}` : 'Not currently clocked in'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex space-x-2">
                  <Button 
                    onClick={handleClockIn}
                    disabled={clockedIn}
                    className="flex-1"
                    variant={clockedIn ? "outline" : "default"}
                  >
                    <LogIn className="h-4 w-4 mr-2" />
                    Clock In
                  </Button>
                  <Button 
                    onClick={handleClockOut}
                    disabled={!clockedIn}
                    variant={clockedIn ? "default" : "outline"}
                    className="flex-1"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    End Shift
                  </Button>
                </div>

                {/* Shift Note */}
                {clockedIn && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      Shift Log Notes (Optional)
                    </label>
                    <Textarea
                      value={shiftNote}
                      onChange={(e) => setShiftNote(e.target.value)}
                      placeholder="Add notes for shift handoff and shift log history..."
                      className="min-h-[80px]"
                    />
                    <p className="text-xs text-gray-500">
                      Notes will be saved to shift log history for staff coordination
                    </p>
                  </div>
                )}

                {clockOutTime && (
                  <p className="text-sm text-gray-600">
                    Last clocked out at {clockOutTime}
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Staff Status Overview */}
            <Card className="card-shadow lg:col-span-2">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center space-x-2">
                    <Users className="h-5 w-5 text-indigo-600" />
                    <span>Staff Overview</span>
                  </CardTitle>
                </div>
                <Button 
                  onClick={() => setShowTimeReports(true)}
                  variant="outline"
                  size="sm"
                >
                  <BarChart3 className="h-4 w-4 mr-2" />
                  View Reports
                </Button>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">{allStaff.length}</div>
                    <div className="text-sm text-gray-600">Total Staff</div>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">
                      {allStaff.filter(staff => staff.role === 'admin').length}
                    </div>
                    <div className="text-sm text-gray-600">Administrators</div>
                  </div>
                  <div className="text-center p-4 bg-indigo-50 rounded-lg">
                    <div className="text-2xl font-bold text-indigo-600">{timeRecords.length}</div>
                    <div className="text-sm text-gray-600">Time Records</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Recent Shift Notes */}
          <Card className="card-shadow">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <FileText className="h-5 w-5 text-indigo-600" />
                <span>Recent Shift Notes</span>
              </CardTitle>
              <CardDescription>
                View shift notes from recent shifts to stay informed
              </CardDescription>
            </CardHeader>
            <CardContent>
              {timeRecords && timeRecords.length > 0 ? (
                <div className="space-y-4 max-h-64 overflow-y-auto">
                  {timeRecords
                    .filter(record => record.shift_note && record.shift_note.trim())
                    .slice(-5) // Show last 5 shift notes
                    .reverse() // Most recent first
                    .map((record, index) => (
                      <div key={record.id || index} className="border-l-4 border-blue-500 pl-4 py-2 bg-blue-50 rounded-r">
                        <div className="flex justify-between items-start mb-1">
                          <span className="font-semibold text-sm text-blue-800">
                            {record.staff_name}
                          </span>
                          <span className="text-xs text-gray-500">
                            {record.date} • {record.shift_type || 'Day'} Shift
                          </span>
                        </div>
                        <p className="text-sm text-gray-700">{record.shift_note}</p>
                      </div>
                    ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p>No recent shift notes available</p>
                  <p className="text-sm">Shift notes will appear here when staff add them</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Staff Directory Tab */}
        <TabsContent value="staff" className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900">👥 Staff Directory</h2>
            {user?.role === 'admin' && (
              <Dialog open={showAddStaffDialog} onOpenChange={(open) => {
                setShowAddStaffDialog(open);
                if (!open) {
                  // Clear form when closing
                  setStaffProfile({
                    username: '',
                    email: '',
                    phone: '',
                    full_name: '',
                    password: '',
                    role: 'staff'
                  });
                }
              }}>
                <DialogTrigger asChild>
                  <Button onClick={() => {
                    // Clear form for new staff
                    setStaffProfile({
                      username: '',
                      email: '',
                      phone: '',
                      full_name: '',
                      password: '',
                      role: 'staff'
                    });
                  }}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Staff Member
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{staffProfile.id ? 'Edit Staff Member' : 'Add New Staff Member'}</DialogTitle>
                    <DialogDescription>
                      {staffProfile.id ? 'Update the staff member information' : 'Add a new staff member to the system'}
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={staffProfile.id ? handleUpdateStaff : handleAddStaff} className="space-y-4">
                    <Input
                      placeholder="Full Name"
                      value={staffProfile.full_name}
                      onChange={(e) => setStaffProfile({...staffProfile, full_name: e.target.value})}
                      required
                    />
                    <Input
                      placeholder="Username"
                      value={staffProfile.username}
                      onChange={(e) => setStaffProfile({...staffProfile, username: e.target.value})}
                      required
                    />
                    <Input
                      type="password"
                      placeholder="Password"
                      value={staffProfile.password}
                      onChange={(e) => setStaffProfile({...staffProfile, password: e.target.value})}
                      required={!staffProfile.id}
                    />
                    <Input
                      type="email"
                      placeholder="Email (optional)"
                      value={staffProfile.email}
                      onChange={(e) => setStaffProfile({...staffProfile, email: e.target.value})}
                    />
                    <Input
                      placeholder="Phone"
                      value={staffProfile.phone}
                      onChange={(e) => setStaffProfile({...staffProfile, phone: e.target.value})}
                    />
                    <Select 
                      value={staffProfile.role} 
                      onValueChange={(value) => setStaffProfile({...staffProfile, role: value})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="staff">Staff</SelectItem>
                        <SelectItem value="admin">Administrator</SelectItem>
                        <SelectItem value="case_worker">Case Manager</SelectItem>
                        <SelectItem value="kitchen_staff">Kitchen Staff</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button type="submit" className="w-full">
                      {staffProfile.id ? 'Update Staff Member' : 'Add Staff Member'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {allStaff.map((staff) => (
              <Card key={staff.id} className="card-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="h-12 w-12 bg-indigo-100 rounded-full flex items-center justify-center">
                        <User className="h-6 w-6 text-indigo-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{staff.full_name}</h3>
                        <p className="text-sm text-gray-600">@{staff.username}</p>
                      </div>
                    </div>
                    <Badge className={staff.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}>
                      {staff.role.replace('_', ' ').toUpperCase()}
                    </Badge>
                  </div>
                  <div className="space-y-2 text-sm">
                    {staff.email && (
                      <div className="flex items-center text-gray-600">
                        <Mail className="h-4 w-4 mr-2" />
                        {staff.email}
                      </div>
                    )}
                    {staff.phone && (
                      <div className="flex items-center text-gray-600">
                        <Phone className="h-4 w-4 mr-2" />
                        {staff.phone}
                      </div>
                    )}
                  </div>
                  
                  {/* Admin Control Buttons */}
                  {user?.role === 'admin' && (
                    <div className="mt-4 flex flex-col space-y-2">
                      <div className="flex space-x-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditStaff(staff)}
                          className="flex-1 text-green-600 border-green-600 hover:bg-green-50"
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          Edit Staff
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteStaff(staff.id)}
                          className="flex-1 text-red-600 border-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </Button>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleResetPassword(staff)}
                        className="w-full text-blue-600 border-blue-600 hover:bg-blue-50"
                      >
                        <Shield className="h-4 w-4 mr-2" />
                        Reset Password
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Schedules Tab */}
        <TabsContent value="schedules" className="space-y-6">
          <div className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <h3 className="text-lg font-medium text-gray-900">Staff Schedules</h3>
              
              <div className="flex items-center space-x-4">
                <Button onClick={fetchSchedules} variant="outline" size="sm">
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
                <Button 
                  onClick={() => printStaffSchedules()} 
                  variant="outline" 
                  size="sm"
                  className="border-green-200 text-green-700 hover:bg-green-50"
                >
                  <Printer className="h-4 w-4 mr-2" />
                  Print Schedules
                </Button>
                <Button onClick={() => {
                  // Clear editing state for new schedule
                  setEditingSchedule(null);
                  setNewSchedule({
                    staff_name: '',
                    date: '',
                    shift: '',
                    task: ''
                  });
                  setShowAddScheduleDialog(true);
                }} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Schedule
                </Button>
              </div>
            </div>

            {/* Schedule View Tabs */}
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8">
                <button
                  onClick={() => setScheduleView('list')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    scheduleView === 'list'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  📋 List View
                </button>
                <button
                  onClick={() => setScheduleView('chart')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    scheduleView === 'chart'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  📊 Chart View
                </button>
              </nav>
            </div>

            {/* Add Schedule Dialog */}
            <Dialog open={showAddScheduleDialog} onOpenChange={(open) => {
              setShowAddScheduleDialog(open);
              if (!open) {
                // Clear editing state when closing dialog
                setEditingSchedule(null);
                setNewSchedule({
                  staff_name: '',
                  date: '',
                  shift: '',
                  task: ''
                });
              }
            }}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingSchedule ? 'Edit Schedule' : 'Add New Schedule'}</DialogTitle>
                  <DialogDescription>
                    {editingSchedule ? 'Edit the existing schedule entry' : 'Create a new schedule entry for staff'}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={editingSchedule ? handleUpdateSchedule : handleAddSchedule} className="space-y-4">
                  <Select 
                    value={newSchedule.staff_name} 
                    onValueChange={(value) => setNewSchedule({...newSchedule, staff_name: value})}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Staff Member" />
                    </SelectTrigger>
                    <SelectContent position="item">
                      {allStaff.map((staff) => (
                        <SelectItem key={staff.id} value={staff.full_name}>
                          {staff.full_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-2">
                      Schedule Date
                    </label>
                    <Input
                      type="date"
                      value={newSchedule.date}
                      onChange={(e) => setNewSchedule({...newSchedule, date: e.target.value})}
                      required
                      min={new Date().toISOString().split('T')[0]} // Prevent past dates
                      className="w-full"
                    />
                  </div>

                  <Select 
                    value={newSchedule.shift} 
                    onValueChange={(value) => setNewSchedule({...newSchedule, shift: value})}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Shift" />
                    </SelectTrigger>
                    <SelectContent position="item">
                      <SelectItem value="morning">Morning (6AM - 2PM)</SelectItem>
                      <SelectItem value="day">Day (8AM - 4PM)</SelectItem>
                      <SelectItem value="evening">Evening (4PM - 12AM)</SelectItem>
                      <SelectItem value="night">Night (12AM - 8AM)</SelectItem>
                      <SelectItem value="volunteer">Volunteer</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>

                  <Input
                    placeholder="Task/Assignment"
                    value={newSchedule.task}
                    onChange={(e) => setNewSchedule({...newSchedule, task: e.target.value})}
                    required
                  />
                  
                  <Button type="submit" className="w-full">
                    <Save className="h-4 w-4 mr-2" />
                    {editingSchedule ? 'Update Schedule' : 'Add Schedule'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="space-y-6">
            {/* Schedule Content based on view */}
            {scheduleView === 'chart' ? (
              // Visual Chart View
              <div className="space-y-6">
                <div className="bg-white rounded-lg border p-6">
                  <h4 className="text-lg font-semibold mb-4 text-gray-900">📊 Weekly Schedule Chart</h4>
                  
                  {/* Chart Legend */}
                  <div className="flex flex-wrap gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 rounded bg-yellow-100 border border-yellow-300"></div>
                      <span className="text-sm font-medium">Morning (8am-4pm)</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 rounded bg-orange-100 border border-orange-300"></div>
                      <span className="text-sm font-medium">Evening (4pm-12am)</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 rounded bg-purple-100 border border-purple-300"></div>
                      <span className="text-sm font-medium">Night (12am-8am)</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 rounded bg-green-100 border border-green-300"></div>
                      <span className="text-sm font-medium">Volunteer</span>
                    </div>
                  </div>

                  {/* Chart Grid */}
                  {(() => {
                    // Create chart data structure
                    const chartData = {};
                    const today = new Date();
                    const dates = [];
                    
                    // Generate next 7 days
                    for (let i = 0; i < 7; i++) {
                      const date = new Date(today);
                      date.setDate(today.getDate() + i);
                      const dateStr = date.toISOString().split('T')[0];
                      dates.push({
                        date: dateStr,
                        dayName: date.toLocaleDateString('en-US', { weekday: 'short' }),
                        dayNum: date.getDate()
                      });
                    }

                    // Group schedules by staff for chart
                    const staffSchedules = {};
                    schedules.forEach(schedule => {
                      if (!staffSchedules[schedule.staff_name]) {
                        staffSchedules[schedule.staff_name] = {};
                      }
                      if (!staffSchedules[schedule.staff_name][schedule.date]) {
                        staffSchedules[schedule.staff_name][schedule.date] = [];
                      }
                      staffSchedules[schedule.staff_name][schedule.date].push(schedule);
                    });

                    return (
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse border border-gray-300">
                          <thead>
                            <tr className="bg-gray-100">
                              <th className="border border-gray-300 p-2 text-left font-semibold">Staff</th>
                              {dates.map(day => (
                                <th key={day.date} className="border border-gray-300 p-2 text-center font-semibold min-w-[120px]">
                                  <div className="text-sm">{day.dayName}</div>
                                  <div className="text-xs text-gray-600">{day.dayNum}</div>
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(staffSchedules).map(([staffName, staffDates]) => (
                              <tr key={staffName} className="hover:bg-gray-50">
                                <td className="border border-gray-300 p-2 font-medium text-gray-900">
                                  {staffName}
                                </td>
                                {dates.map(day => {
                                  const daySchedules = staffDates[day.date] || [];
                                  return (
                                    <td key={day.date} className="border border-gray-300 p-1">
                                      <div className="space-y-1">
                                        {daySchedules.map((schedule, idx) => {
                                          const shiftData = getShiftTimes(schedule.shift_type || schedule.shift);
                                          return (
                                            <div key={idx} className={`text-xs p-1 rounded ${shiftData.color} relative group`}>
                                              <div className="font-medium">{shiftData.display}</div>
                                              <div className="flex justify-between items-center">
                                                <span className="text-xs">
                                                  {(schedule.shift_type || schedule.shift).charAt(0).toUpperCase() + 
                                                   (schedule.shift_type || schedule.shift).slice(1)}
                                                </span>
                                                {/* Call-in Button */}
                                                <button
                                                  onClick={() => {
                                                    setSelectedScheduleForCallIn(schedule);
                                                    setShowCallInDialog(true);
                                                  }}
                                                  className="opacity-0 group-hover:opacity-100 transition-opacity bg-white rounded-full p-1 hover:bg-gray-100"
                                                  title="Mark Call-in"
                                                >
                                                  📞
                                                </button>
                                                {/* Replacement Button - only for admins if there's a call-in */}
                                                {user?.role === 'admin' && schedule.called_in_status && (
                                                  <button
                                                    onClick={() => {
                                                      setSelectedScheduleForReplacement(schedule);
                                                      setShowReplacementDialog(true);
                                                    }}
                                                    className="opacity-0 group-hover:opacity-100 transition-opacity bg-white rounded-full p-1 hover:bg-gray-100 ml-1"
                                                    title="Assign Replacement"
                                                  >
                                                    🔄
                                                  </button>
                                                )}
                                              </div>
                                              {schedule.called_in_status && (
                                                <div className="text-xs mt-1 bg-yellow-200 text-yellow-800 px-1 rounded">
                                                  📞 {schedule.called_in_status.replace('_', ' ').toUpperCase()}
                                                </div>
                                              )}
                                              {schedule.replacement_staff_name && (
                                                <div className="text-xs mt-1 bg-green-200 text-green-800 px-1 rounded">
                                                  🔄 {schedule.replacement_staff_name}
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })}
                                        {daySchedules.length === 0 && (
                                          <div className="text-xs text-gray-400 p-1">No shifts</div>
                                        )}
                                      </div>
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                            {Object.keys(staffSchedules).length === 0 && (
                              <tr>
                                <td colSpan={8} className="border border-gray-300 p-8 text-center text-gray-500">
                                  No schedules found. Create some schedules to see the chart.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    );
                  })()}
                </div>
              </div>
            ) : (
              // List View (existing layout)
              <div>
                {/* Group schedules by staff member and date */}
            {(() => {
              // Group schedules by staff_name and date
              const groupedSchedules = {};
              schedules.forEach(schedule => {
                const key = `${schedule.staff_name}_${schedule.date}`;
                if (!groupedSchedules[key]) {
                  groupedSchedules[key] = {
                    staff_name: schedule.staff_name,
                    date: schedule.date,
                    shifts: []
                  };
                }
                groupedSchedules[key].shifts.push(schedule);
              });

              // Sort shifts within each group by start time
              Object.values(groupedSchedules).forEach(group => {
                group.shifts.sort((a, b) => {
                  const aTime = a.start_time || '00:00';
                  const bTime = b.start_time || '00:00';
                  return aTime.localeCompare(bTime);
                });
              });

              const sortedGroups = Object.values(groupedSchedules).sort((a, b) => {
                // Sort by date first, then by staff name
                const dateCompare = a.date.localeCompare(b.date);
                if (dateCompare !== 0) return dateCompare;
                return a.staff_name.localeCompare(b.staff_name);
              });

              return (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {sortedGroups.map((group, index) => (
                    <Card key={`${group.date}-${group.staff_name}`} className="card-shadow">
                      <CardContent className="p-6">
                        {/* Staff and Date Header */}
                        <div className="mb-4 pb-3 border-b border-gray-200">
                          <h3 className="font-semibold text-lg text-gray-900">{group.staff_name}</h3>
                          <p className="text-sm font-medium text-blue-600">
                            📅 {(() => {
                              const [year, month, day] = group.date.split('-');
                              const localDate = new Date(year, month - 1, day);
                              return localDate.toLocaleDateString('en-US', { 
                                weekday: 'long', 
                                year: 'numeric', 
                                month: 'long', 
                                day: 'numeric' 
                              });
                            })()}
                          </p>
                        </div>

                        {/* Multiple Shifts for this staff/date */}
                        <div className="space-y-4">
                          {group.shifts.map((schedule, shiftIndex) => {
                            const shiftData = getShiftTimes(schedule.shift_type || schedule.shift);
                            return (
                            <div key={schedule.id} className={`border rounded-lg p-4 ${shiftData.color}`}>
                              <div className="flex justify-between items-start mb-2">
                                <div className="flex-1">
                                  <div className="flex items-center space-x-2 mb-1">
                                    <Clock className="h-4 w-4" />
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                                      Shift {shiftIndex + 1}
                                    </span>
                                    <span className="text-sm font-medium">
                                      {(schedule.shift_type || schedule.shift).charAt(0).toUpperCase() + 
                                       (schedule.shift_type || schedule.shift).slice(1)}
                                    </span>
                                  </div>
                                  <p className="text-sm font-medium">
                                    🕐 {shiftData.display}
                                  </p>
                                  {(schedule.notes || schedule.task) && (
                                    <p className="text-xs text-gray-500 mt-1">
                                      📝 {schedule.notes || schedule.task}
                                    </p>
                                  )}
                                  
                                  {/* Call-in Status Display */}
                                  {schedule.called_in_status && (
                                    <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded">
                                      <p className="text-xs font-medium text-yellow-800">
                                        📞 Call-in: {schedule.called_in_status.replace('_', ' ').toUpperCase()}
                                      </p>
                                      {schedule.called_in_notes && (
                                        <p className="text-xs text-yellow-700 mt-1">
                                          💬 {schedule.called_in_notes}
                                        </p>
                                      )}
                                    </div>
                                  )}
                                  
                                  {/* Replacement Staff Display */}
                                  {schedule.replacement_staff_name && (
                                    <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded">
                                      <p className="text-xs font-medium text-green-800">
                                        🔄 Replacement: {schedule.replacement_staff_name}
                                      </p>
                                      <p className="text-xs text-green-700">
                                        ✅ Assigned by admin
                                      </p>
                                    </div>
                                  )}
                                </div>

                                {/* Edit/Delete buttons for each shift */}
                                {(user?.role === 'admin' || user?.role === 'staff') && (
                                  <div className="flex space-x-1 ml-2">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => {
                                        setSelectedScheduleForCallIn(schedule);
                                        setShowCallInDialog(true);
                                      }}
                                      className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700"
                                      title="Mark Call-in"
                                    >
                                      📞
                                    </Button>
                                    {/* Replacement button - only show for admins if there's a call-in */}
                                    {user?.role === 'admin' && schedule.called_in_status && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => {
                                          setSelectedScheduleForReplacement(schedule);
                                          setShowReplacementDialog(true);
                                        }}
                                        className="h-8 w-8 p-0 text-green-600 hover:text-green-700"
                                        title="Assign Replacement"
                                      >
                                        🔄
                                      </Button>
                                    )}
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleEditSchedule(schedule)}
                                      className="h-8 w-8 p-0"
                                    >
                                      <Edit className="h-3 w-3" />
                                    </Button>
                                    <Button
                                      size="sm" 
                                      variant="outline"
                                      onClick={() => handleDeleteSchedule(schedule.id)}
                                      className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                          })}
                        </div>

                        {/* Summary for multiple shifts */}
                        {group.shifts.length > 1 && (
                          <div className="mt-4 pt-3 border-t border-gray-200">
                            <p className="text-xs text-gray-500">
                              📊 Total: {group.shifts.length} shifts • 
                              {(() => {
                                const totalMinutes = group.shifts.reduce((total, shift) => {
                                  const start = shift.start_time.split(':');
                                  const end = shift.end_time.split(':');
                                  const startMinutes = parseInt(start[0]) * 60 + parseInt(start[1]);
                                  let endMinutes = parseInt(end[0]) * 60 + parseInt(end[1]);
                                  if (endMinutes <= startMinutes) endMinutes += 24 * 60;
                                  return total + (endMinutes - startMinutes);
                                }, 0);
                                const hours = Math.floor(totalMinutes / 60);
                                const minutes = totalMinutes % 60;
                                return `${hours}h ${minutes}m total`;
                              })()}
                            </p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              );
            })()}

            {schedules.length === 0 && (
              <Card>
                <CardContent className="p-8 text-center">
                  <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Schedules Yet</h3>
                  <p className="text-gray-600">Start by creating your first schedule entry.</p>
                </CardContent>
              </Card>
            )}
            </div>
            )}
          </div>
        </TabsContent>

        {/* Call-in Dialog */}
        <Dialog open={showCallInDialog} onOpenChange={setShowCallInDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>📞 Mark Call-in Status</DialogTitle>
              <DialogDescription>
                Update call-in status for {selectedScheduleForCallIn?.staff_name} - 
                {selectedScheduleForCallIn && (() => {
                  const [year, month, day] = selectedScheduleForCallIn.date.split('-');
                  const localDate = new Date(year, month - 1, day);
                  return localDate.toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    month: 'short', 
                    day: 'numeric' 
                  });
                })()}
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={(e) => { e.preventDefault(); handleCallInSubmit(); }} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Call-in Status *</label>
                <Select 
                  value={callInData.status} 
                  onValueChange={(value) => setCallInData({...callInData, status: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select call-in status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="called_in">✅ Called In - Present</SelectItem>
                    <SelectItem value="no_show">❌ No Show</SelectItem>
                    <SelectItem value="late">⏰ Late</SelectItem>
                    <SelectItem value="sick">🤒 Sick</SelectItem>
                    <SelectItem value="emergency">🚨 Emergency</SelectItem>
                    <SelectItem value="scheduled_off">📅 Scheduled Off</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">Date/Time</label>
                <Input
                  type="datetime-local"
                  value={callInData.called_in_at}
                  onChange={(e) => setCallInData({...callInData, called_in_at: e.target.value})}
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">Notes (optional)</label>
                <Textarea
                  value={callInData.notes}
                  onChange={(e) => setCallInData({...callInData, notes: e.target.value})}
                  placeholder="Additional notes about the call-in (e.g., reason for lateness, expected arrival time, etc.)"
                  rows={3}
                />
              </div>

              <div className="bg-blue-50 p-3 rounded-lg">
                <div className="text-sm text-blue-800">
                  <strong>Shift Details:</strong>
                  <br />👤 {selectedScheduleForCallIn?.staff_name}
                  <br />🕐 {selectedScheduleForCallIn && getShiftTimes(selectedScheduleForCallIn.shift_type || selectedScheduleForCallIn.shift).display}
                  <br />📋 {selectedScheduleForCallIn?.shift_type || selectedScheduleForCallIn?.shift} shift
                </div>
              </div>
              
              <div className="flex justify-end space-x-3">
                <Button type="button" variant="outline" onClick={cancelCallIn}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                  📞 Update Call-in Status
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Community Directory Tab */}
        <TabsContent value="community" className="space-y-6">
          <Card className="card-shadow">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>📞 Community Directory</CardTitle>
                <CardDescription>Community services and resources</CardDescription>
              </div>
              {user?.role === 'admin' && (
                <Dialog open={showAddServiceDialog} onOpenChange={setShowAddServiceDialog}>
                  <DialogTrigger asChild>
                    <Button>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Service
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add Community Service</DialogTitle>
                      <DialogDescription>
                        Add a new community service to the directory
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleAddService} className="space-y-4">
                      <Input
                        placeholder="Service Name"
                        value={newService.name}
                        onChange={(e) => setNewService({...newService, name: e.target.value})}
                        required
                      />
                      <Select 
                        value={newService.category} 
                        onValueChange={(value) => setNewService({...newService, category: value})}
                        required
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select Category" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="housing">Housing</SelectItem>
                          <SelectItem value="healthcare">Healthcare</SelectItem>
                          <SelectItem value="employment">Employment</SelectItem>
                          <SelectItem value="mental_health">Mental Health</SelectItem>
                          <SelectItem value="substance_abuse">Substance Abuse</SelectItem>
                          <SelectItem value="legal">Legal Services</SelectItem>
                          <SelectItem value="benefits">Benefits</SelectItem>
                          <SelectItem value="food">Food Services</SelectItem>
                          <SelectItem value="transportation">Transportation</SelectItem>
                          <SelectItem value="education">Education</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                      <Textarea
                        placeholder="Description"
                        value={newService.description}
                        onChange={(e) => setNewService({...newService, description: e.target.value})}
                        required
                      />
                      <Input
                        placeholder="Phone Number"
                        value={newService.phone}
                        onChange={(e) => setNewService({...newService, phone: e.target.value})}
                      />
                      <Input
                        type="email"
                        placeholder="Email Address"
                        value={newService.email}
                        onChange={(e) => setNewService({...newService, email: e.target.value})}
                      />
                      <Input
                        placeholder="Address"
                        value={newService.address}
                        onChange={(e) => setNewService({...newService, address: e.target.value})}
                      />
                      <Input
                        placeholder="Website URL"
                        value={newService.website}
                        onChange={(e) => setNewService({...newService, website: e.target.value})}
                      />
                      <Button type="submit" className="w-full">Add Community Service</Button>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
            </CardHeader>
            <CardContent>
              {/* Confirmation Dialog */}
              {showDeleteConfirm && (
                <>
                  <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    zIndex: 999
                  }} onClick={cancelDelete} />
                  
                  <div style={{ 
                    position: 'fixed', 
                    top: '50%', 
                    left: '50%', 
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: 'white', 
                    border: '3px solid red', 
                    padding: '20px', 
                    zIndex: 1000,
                    borderRadius: '10px',
                    boxShadow: '0 0 20px rgba(0,0,0,0.5)'
                  }}>
                    <h3 style={{ color: 'red', marginBottom: '10px' }}>Confirm Delete</h3>
                    <p>Are you sure you want to delete <strong>{serviceToDelete?.name}</strong>?</p>
                    <p style={{ color: 'red', fontSize: '14px' }}>This action cannot be undone.</p>
                    <div style={{ marginTop: '15px', display: 'flex', gap: '10px' }}>
                      <button 
                        onClick={confirmDelete}
                        style={{ 
                          backgroundColor: 'red', 
                          color: 'white', 
                          padding: '10px 20px', 
                          border: 'none', 
                          borderRadius: '5px',
                          cursor: 'pointer',
                          fontWeight: 'bold'
                        }}
                      >
                        ✅ Yes, Delete
                      </button>
                      <button 
                        onClick={cancelDelete}
                        style={{ 
                          backgroundColor: 'gray', 
                          color: 'white', 
                          padding: '10px 20px', 
                          border: 'none', 
                          borderRadius: '5px',
                          cursor: 'pointer'
                        }}
                      >
                        ❌ Cancel
                      </button>
                    </div>
                  </div>
                </>
              )}

              <div style={{ backgroundColor: 'lightblue', color: 'darkblue', padding: '10px', marginBottom: '20px', borderRadius: '5px' }}>
                <p>📊 Community Services Directory • {communityServices.length} services available</p>
              </div>

              {communityServices.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {communityServices.map((service) => (
                    <div key={service.id} className="border rounded-lg p-4 bg-white shadow">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h3 className="font-semibold text-lg">{service.name}</h3>
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                            {service.category.replace('_', ' ').toUpperCase()}
                          </span>
                        </div>
                        
                        {/* Delete Button - Only for Admin */}
                        {user?.role === 'admin' && (
                          <button
                            onClick={() => handleDeleteClick(service)}
                            className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-sm font-semibold"
                            title="Delete Service"
                          >
                            🗑️ Delete
                          </button>
                        )}
                      </div>
                      
                      <p className="text-sm text-gray-600 mb-2">{service.description}</p>
                      
                      {service.phone && (
                        <p className="text-sm">📞 {service.phone}</p>
                      )}
                      {service.email && (
                        <p className="text-sm">✉️ {service.email}</p>
                      )}
                      {service.address && (
                        <p className="text-sm">📍 {service.address}</p>
                      )}
                      {service.website && (
                        <p className="text-sm">🌐 <a href={service.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{service.website}</a></p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Globe className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Community Services Yet</h3>
                  <p className="text-gray-600 mb-4">Start building your community resource directory by adding local services.</p>
                  {user?.role === 'admin' && (
                    <Button onClick={() => setShowAddServiceDialog(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Your First Service
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Time Reports Dialog */}
      <Dialog open={showTimeReports} onOpenChange={setShowTimeReports}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <BarChart3 className="h-5 w-5" />
              <span>Time Reports</span>
            </DialogTitle>
            <DialogDescription>
              Staff time tracking reports for payroll
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-600">
                Showing {timeRecords.length} time records
              </p>
              <Button onClick={exportTimeReports} size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            </div>
            
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="border border-gray-300 px-4 py-2 text-left">Staff</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Date</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Clock In</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Clock Out</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Hours</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Shift</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {timeRecords.map((record, index) => (
                    <tr key={record.id || index} className="hover:bg-gray-50">
                      <td className="border border-gray-300 px-4 py-2">{record.staff_name}</td>
                      <td className="border border-gray-300 px-4 py-2">{record.date}</td>
                      <td className="border border-gray-300 px-4 py-2">{record.clock_in_time}</td>
                      <td className="border border-gray-300 px-4 py-2">{record.clock_out_time || 'In Progress'}</td>
                      <td className="border border-gray-300 px-4 py-2">{record.hours_worked || 'In Progress'}</td>
                      <td className="border border-gray-300 px-4 py-2">{record.shift_type || 'Day'}</td>
                      <td className="border border-gray-300 px-4 py-2 text-sm">{record.shift_note || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Shift Note Dialog */}
      <Dialog open={showShiftNoteDialog} onOpenChange={setShowShiftNoteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>End Shift & Add Shift Log</DialogTitle>
            <DialogDescription>
              Add notes about your shift. This will be recorded in the shift log for the next shift to review.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">
                Shift Notes & Handoff Information
              </label>
              <Textarea
                value={shiftNote}
                onChange={(e) => setShiftNote(e.target.value)}
                placeholder="Describe what happened during your shift, any incidents, tasks completed, or important information for the next shift..."
                className="min-h-[120px]"
              />
              <p className="text-xs text-gray-500 mt-1">
                These notes will be saved to the shift log history for staff coordination.
              </p>
            </div>
            <div className="flex justify-end space-x-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShiftNote('');
                  setShowShiftNoteDialog(false);
                }}
              >
                Skip Notes
              </Button>
              <Button onClick={handleShiftNoteSubmit}>
                <Save className="h-4 w-4 mr-2" />
                End Shift & Save Log
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={showPasswordResetDialog} onOpenChange={setShowPasswordResetDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Reset password for {staffToReset?.full_name} (@{staffToReset?.username})
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">
                New Password
              </label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (minimum 6 characters)"
                className="w-full"
              />
              <p className="text-xs text-gray-500 mt-1">
                Password must be at least 6 characters long
              </p>
            </div>
            <div className="flex justify-end space-x-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowPasswordResetDialog(false);
                  setStaffToReset(null);
                  setNewPassword('');
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={handlePasswordReset}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Shield className="h-4 w-4 mr-2" />
                Reset Password
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Schedule Confirmation Dialog */}
      <Dialog open={showDeleteScheduleDialog} onOpenChange={setShowDeleteScheduleDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete Schedule</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this schedule? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {scheduleToDelete && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <div className="space-y-2">
                <p className="font-medium text-red-800">
                  📅 {scheduleToDelete.staff_name}
                </p>
                <p className="text-sm text-red-700">
                  {scheduleToDelete.date ? (() => {
                    const [year, month, day] = scheduleToDelete.date.split('-');
                    const localDate = new Date(year, month - 1, day);
                    return localDate.toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    });
                  })() : 'Date not set'}
                </p>
                <p className="text-sm text-red-700">
                  🕐 {scheduleToDelete.shift_type || scheduleToDelete.shift} Shift 
                  ({scheduleToDelete.start_time} - {scheduleToDelete.end_time})
                </p>
                {(scheduleToDelete.notes || scheduleToDelete.task) && (
                  <p className="text-sm text-red-600">
                    📝 {scheduleToDelete.notes || scheduleToDelete.task}
                  </p>
                )}
              </div>
            </div>
          )}
          <div className="flex justify-end space-x-2">
            <Button 
              variant="outline" 
              onClick={cancelDeleteSchedule}
            >
              Cancel
            </Button>
            <Button 
              onClick={confirmDeleteSchedule}
              className="bg-red-600 hover:bg-red-700"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Schedule
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Call-in Dialog */}
      <Dialog open={showCallInDialog} onOpenChange={setShowCallInDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-blue-600">📞 Mark Call-in Status</DialogTitle>
            <DialogDescription>
              Update the call-in status for this scheduled shift
            </DialogDescription>
          </DialogHeader>
          {selectedScheduleForCallIn && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="space-y-2">
                <p className="font-medium text-blue-800">
                  👤 {selectedScheduleForCallIn.staff_name}
                </p>
                <p className="text-sm text-blue-700">
                  📅 {selectedScheduleForCallIn.date ? (() => {
                    const [year, month, day] = selectedScheduleForCallIn.date.split('-');
                    const localDate = new Date(year, month - 1, day);
                    return localDate.toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    });
                  })() : 'Date not set'}
                </p>
                <p className="text-sm text-blue-700">
                  🕐 {(selectedScheduleForCallIn.shift_type || selectedScheduleForCallIn.shift)} Shift 
                  ({selectedScheduleForCallIn.start_time} - {selectedScheduleForCallIn.end_time})
                </p>
              </div>
            </div>
          )}
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">
                Call-in Status
              </label>
              <Select 
                value={callInData.status} 
                onValueChange={(value) => setCallInData({...callInData, status: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="called_in">✅ Called In</SelectItem>
                  <SelectItem value="no_show">❌ No Show</SelectItem>
                  <SelectItem value="late">⏰ Late</SelectItem>
                  <SelectItem value="sick">🤒 Called In Sick</SelectItem>
                  <SelectItem value="emergency">🚨 Emergency</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">
                Call-in Time
              </label>
              <Input
                type="datetime-local"
                value={callInData.called_in_at}
                onChange={(e) => setCallInData({...callInData, called_in_at: e.target.value})}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">
                Notes (Optional)
              </label>
              <Textarea
                value={callInData.notes}
                onChange={(e) => setCallInData({...callInData, notes: e.target.value})}
                placeholder="Add any additional notes about the call-in..."
                className="min-h-[80px]"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-2 mt-6">
            <Button 
              variant="outline" 
              onClick={cancelCallIn}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCallInSubmit}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Phone className="h-4 w-4 mr-2" />
              Update Call-in Status
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Replacement Staff Dialog */}
      <Dialog open={showReplacementDialog} onOpenChange={setShowReplacementDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-green-600">🔄 Assign Replacement Staff</DialogTitle>
            <DialogDescription>
              Assign replacement staff for {selectedScheduleForReplacement?.staff_name} - 
              {selectedScheduleForReplacement && (() => {
                const [year, month, day] = selectedScheduleForReplacement.date.split('-');
                const localDate = new Date(year, month - 1, day);
                return localDate.toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  month: 'short', 
                  day: 'numeric' 
                });
              })()}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={(e) => { e.preventDefault(); handleReplacementSubmit(); }} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">
                Select Replacement Staff *
              </label>
              <Select 
                value={replacementData.replacement_staff_id} 
                onValueChange={(value) => {
                  const selectedStaff = allStaff.find(staff => staff.id === value);
                  setReplacementData({
                    ...replacementData, 
                    replacement_staff_id: value,
                    replacement_staff_name: selectedStaff?.full_name || ''
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose replacement staff member" />
                </SelectTrigger>
                <SelectContent>
                  {allStaff.filter(staff => staff.id !== selectedScheduleForReplacement?.staff_id).map((staff) => (
                    <SelectItem key={staff.id} value={staff.id}>
                      {staff.full_name} ({staff.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">
                Notes (Optional)
              </label>
              <Textarea
                value={replacementData.notes}
                onChange={(e) => setReplacementData({...replacementData, notes: e.target.value})}
                placeholder="Additional notes about the replacement assignment..."
                className="min-h-[80px]"
              />
            </div>

            <div className="bg-green-50 p-3 rounded-lg">
              <div className="text-sm text-green-800">
                <strong>Original Schedule:</strong>
                <br />👤 {selectedScheduleForReplacement?.staff_name}
                <br />🕐 {selectedScheduleForReplacement && getShiftTimes(selectedScheduleForReplacement.shift_type || selectedScheduleForReplacement.shift).display}
                <br />📋 {selectedScheduleForReplacement?.shift_type || selectedScheduleForReplacement?.shift} shift
                <br />📞 Status: {selectedScheduleForReplacement?.called_in_status || 'No status'}
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <Button type="button" variant="outline" onClick={cancelReplacement}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-green-600 hover:bg-green-700"
                disabled={!replacementData.replacement_staff_id}
              >
                🔄 Assign Replacement
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffManagement;